import {useEffect, useState} from 'react';
import {AnimatePresence, m} from 'framer-motion';
import {ProgressCircle} from '@common/ui/progress/progress-circle';
import {GroupIcon} from '@common/icons/material/Group';
import {useAuth} from '@common/auth/use-auth';
import {
  ChipField,
  ChipValue,
} from '@common/ui/forms/input-field/chip-field/chip-field';
import {useValueLists} from '@common/http/value-lists';
import {Button} from '@common/ui/buttons/button';
import {ArrowDropDownIcon} from '@common/icons/material/ArrowDropDown';
import {ConfirmationDialog} from '@common/ui/overlays/dialog/confirmation-dialog';
import {isEmail} from '@common/utils/string/is-email';
import {ButtonSize} from '@common/ui/buttons/button-size';
import {IconButton} from '@common/ui/buttons/icon-button';
import {CloseIcon} from '@common/icons/material/Close';
import {ExitToAppIcon} from '@common/icons/material/ExitToApp';
import {toast} from '@common/ui/toast/toast';
import {DialogTrigger} from '@common/ui/overlays/dialog/dialog-trigger';
import {Menu, MenuItem, MenuTrigger} from '@common/ui/navigation/menu/menu-trigger';
import {useDialogContext} from '@common/ui/overlays/dialog/dialog-context';
import {Dialog} from '@common/ui/overlays/dialog/dialog';
import {DialogHeader} from '@common/ui/overlays/dialog/dialog-header';
import {DialogBody} from '@common/ui/overlays/dialog/dialog-body';
import {Trans} from '@common/i18n/trans';
import {useTrans} from '@common/i18n/use-trans';
import {message} from '@common/i18n/message';
import { Team } from '@app_modules/teams/team';
import { TeamMember } from '@app_modules/teams/team-member';
import { TeamInvite } from '@app_modules/teams/team-invite';
import { useTeamWithMembers } from './team-with-members';
import { useChangeRole } from './change-role';
import { useInviteMembers } from './invite-members';
import { useRemoveMember } from './remove-member';
import { LeaveTeamConfirmation } from '@app_modules/teams/leave-team-confirmation';
import { useResendInvite } from './resend-invite';

interface TeamMembersDialogProps {
  team: Team;
}
export function TeamMembersDialog({
  team,
}: TeamMembersDialogProps) {
  const {data, isLoading} = useTeamWithMembers(team.id);
  
  return (
    <Dialog size="lg">
      <DialogHeader>
        <Trans message="Manage team members" />
      </DialogHeader>
      <DialogBody>
        {isLoading ? (
          <div className="flex min-h-[238px] items-center justify-center">
            <ProgressCircle isIndeterminate aria-label="Loading team..." />
          </div>
        ) : (
          <Manager team={data!.team} />
        )}
      </DialogBody>
    </Dialog>
  );
}

interface ManagerProps {
  team: Team;
}
function Manager({team}: ManagerProps) {
  const {user} = useAuth();
  
  const can = usePermissions(team);


  
  const members: (TeamMember | TeamInvite)[] = [
    ...(team.members || []),
    ...(team.invites || []),
  ];
  const shouldHideOtherMembers = !can.update && !can.delete;

  return (
    <div>
      {can.invite && <InviteChipField team={team} />}
      <div className="mb-14 flex items-center gap-10 text-base">
        <GroupIcon className="icon-sm" />
        <Trans
          message="Members of :team"
          values={{team: team.name}}
        />
      </div>
      <AnimatePresence initial={false}>
        {members.map(member => {
          if (shouldHideOtherMembers && member.id !== user?.id) {
            return null;
          }
          return (
            <MemberListItem
              key={`${member.model_type}.${member.id}`}
              team={team}
              member={member}
            />
          );
        })}
        {shouldHideOtherMembers && (
          <div className="text-muted">
            <Trans
              message="And [one one other member|:count other members]"
              values={{count: members.length}}
            />
          </div>
        )}
      </AnimatePresence>
    </div>
  );
}

interface MemberListItemProps {
  member: TeamMember | TeamInvite;
  team: Team;
}
function MemberListItem({team, member}: MemberListItemProps) {
  return (
    <m.div
      initial={{x: '-100%', opacity: 0}}
      animate={{x: 0, opacity: 1}}
      exit={{x: '100%', opacity: 0}}
      transition={{type: 'tween', duration: 0.125}}
      className="mb-20 flex items-start gap-14 text-sm"
      key={`${member.model_type}.${member.id}`}
    >
      <img
        className="h-36 w-36 flex-shrink-0 rounded"
        src={member.avatar}
        alt=""
      />
      <div className="min-w-0 flex-auto items-center justify-between gap-14 md:flex">
        <div className="mb-10 overflow-hidden md:mb-0 md:mr-10">
          <div className="flex items-center justify-start gap-6">
            <div className="overflow-hidden text-ellipsis whitespace-nowrap">
              {member.display_name}
            </div>
            <MemberDisplayNameAppend team={team} member={member} />
          </div>
          <div className="text-muted">{member.email}</div>
        </div>
        <MemberActions team={team} member={member} />
      </div>
    </m.div>
  );
}

function usePermissions(team: Team) {
  const {user: authUser} = useAuth();
  const response = {update: false, invite: false, delete: false};
  const permissions = ['update', 'invite', 'delete'] as const;
  const authMember = team.members?.find(mb => mb.id === authUser?.id);

  if (authMember) {
    permissions.forEach(permission => {
      
      response[permission] =
        authMember.is_owner ||
        !!authMember.permissions?.find(
          p => p.name === `team_members.${permission}`,
        );
    });
  }

  return response;
}

interface MemberActionsProps {
  team: Team;
  member: TeamMember | TeamInvite;
}
function MemberActions({team, member}: MemberActionsProps) {
  const [selectedRole, setSelectedRole] = useState<number>(member.role_id);
  const changeRole = useChangeRole();
  const {user} = useAuth();

  const can = usePermissions(team);
  const isOwner = member.model_type === 'member' && member.is_owner;
  const isCurrentUser =
    member.model_type === 'member' && user?.id === member.id;

  const roleSelector =
    !can.update || isOwner || isCurrentUser ? (
      <div className="ml-auto text-muted first:capitalize">
        <Trans message={member.role_name} />
      </div>
    ) : (
      <RoleMenuTrigger
        className="ml-auto flex-shrink-0"
        size="xs"
        value={selectedRole}
        isDisabled={changeRole.isPending}
        onChange={roleId => {
          setSelectedRole(roleId);
          changeRole.mutate({
            roleId,
            teamId: team.id,
            member,
          });
        }}
      />
    );

  return (
    <>
      {roleSelector}
      {!isOwner && (isCurrentUser || can.delete) && (
        <RemoveMemberButton
          type={isCurrentUser ? 'leave' : 'remove'}
          member={member}
          team={team}
        />
      )}
    </>
  );
}

interface InviteChipFieldProps {
  team: Team;
}
function InviteChipField({team}: InviteChipFieldProps) {
  const {trans} = useTrans();
  const [chips, setChips] = useState<ChipValue[]>([]);
  const allEmailsValid = chips.every(chip => !chip.invalid);
  const displayWith = (chip: ChipValue) => chip.description || chip.name;
  const [selectedRole, setSelectedRole] = useState<number>();
  const inviteMembers = useInviteMembers();
  const {data} = useValueLists(['teamRoles']);

  useEffect(() => {
    if (!selectedRole && data?.teamRoles?.length) {
      setSelectedRole(data.teamRoles[0].id);
    }
  }, [data, selectedRole]);

  return (
    <div className="mb-30">
      <ChipField
        value={chips}
        onChange={setChips}
        displayWith={displayWith}
        validateWith={chip => {
          const invalid = !isEmail(chip.description);
          return {
            ...chip,
            invalid,
            errorMessage: invalid
              ? trans({message: 'Not a valid email'})
              : undefined,
          };
        }}
        placeholder={trans({message: 'Enter email addresses'})}
        label={<Trans message="Invite people" />}
      />

      <div className="mt-14 flex items-center justify-between gap-14">
        <RoleMenuTrigger onChange={setSelectedRole} value={selectedRole} />
        {chips.length && selectedRole ? (
          <Button
            variant="flat"
            color="primary"
            size="sm"
            disabled={inviteMembers.isPending || !allEmailsValid}
            onClick={() => {
              inviteMembers.mutate(
                {
                  emails: chips.map(c => displayWith(c)),
                  roleId: selectedRole,
                  teamId: team.id,
                },
                {
                  onSuccess: () => {
                    setChips([]);
                  },
                },
              );
            }}
          >
            <Trans message="Invite" />
          </Button>
        ) : null}
      </div>
    </div>
  );
}

interface RemoveMemberButtonProps {
  member: TeamMember | TeamInvite;
  team: Team;
  type: 'leave' | 'remove';
}
function RemoveMemberButton({
  member,
  team,
  type,
}: RemoveMemberButtonProps) {
  const removeMember = useRemoveMember();
  const {close} = useDialogContext();
  return (
    <DialogTrigger
      type="modal"
      onClose={isConfirmed => {
        if (isConfirmed) {
          removeMember.mutate({
            teamId: team.id,
            memberId: member.id,
            memberType: member.model_type,
          });
          if (type === 'leave') {
            close();
            toast(message('Left team'));
          }
        }
      }}
    >
      <IconButton
        size="md"
        className="flex-shrink-0 text-muted"
        disabled={removeMember.isPending}
      >
        {type === 'leave' ? <ExitToAppIcon /> : <CloseIcon />}
      </IconButton>
      {type === 'leave' ? (
        <LeaveTeamConfirmation />
      ) : (
        <RemoveMemberConfirmation member={member} />
      )}
    </DialogTrigger>
  );
}

interface RemoveMemberConfirmationProps {
  member: TeamMember | TeamInvite;
}
function RemoveMemberConfirmation({member}: RemoveMemberConfirmationProps) {
  return (
    <ConfirmationDialog
      isDanger
      title={<Trans message="Remove member" />}
      body={
        <div>
          <Trans
            message="Are you sure you want to remove `:name`?"
            values={{name: member.display_name}}
          />
          <div className="mt-8 font-semibold">
            <Trans
              message="All team resources created by `:name` will be transferred to team owner."
              values={{
                name: member.display_name,
              }}
            />
          </div>
        </div>
      }
      confirm={<Trans message="Remove" />}
    />
  );
}

interface RoleMenuTriggerProps {
  onChange: (value: number) => void;
  value?: number;
  size?: ButtonSize;
  className?: string;
  isDisabled?: boolean;
}
function RoleMenuTrigger({
  value,
  onChange,
  size = 'xs',
  className,
  isDisabled,
}: RoleMenuTriggerProps) {
  const {data} = useValueLists(['teamRoles']);
  const role = data?.teamRoles?.find(r => r.id === value);
  if (!value || !role || !data?.teamRoles) return null;

  return (
    <MenuTrigger
      selectionMode="single"
      selectedValue={value}
      onSelectionChange={newValue => {
        onChange(newValue as number);
      }}
    >
      <Button
        className={className}
        size={size}
        variant="flat"
        color="chip"
        disabled={isDisabled}
        endIcon={<ArrowDropDownIcon />}
      >
        <Trans message={role.name} />
      </Button>
      <Menu>
        {data.teamRoles.map(r => (
          <MenuItem value={r.id} key={r.id} description={r.description}>
            <Trans message={r.name} />
          </MenuItem>
        ))}
      </Menu>
    </MenuTrigger>
  );
}

interface MemberDisplayNameAppendProps {
  member: TeamMember | TeamInvite;
  team: Team;
}
function MemberDisplayNameAppend({
  member,
  team,
}: MemberDisplayNameAppendProps) {
  const {user} = useAuth();
  const can = usePermissions(team);

  if (user?.id === member.id) {
    return (
      <div className="font-medium">
        (<Trans message="You" />)
      </div>
    );
  }
  if (member.model_type === 'invite') {
    return (
      <div className="flex items-center gap-4">
        <div>·</div>
        <div className="font-medium">
          <Trans message="Invited" />
        </div>
        {can.invite ? (
          <>
            <div>·</div>
            <ResendInviteDialogTrigger member={member} team={team} />
          </>
        ) : null}
      </div>
    );
  }
  return null;
}

function ResendInviteDialogTrigger({
  member,
  team,
}: MemberDisplayNameAppendProps) {
  const resendInvite = useResendInvite();
  return (
    <DialogTrigger
      type="modal"
      onClose={isConfirmed => {
        if (isConfirmed) {
          resendInvite.mutate({
            teamId: team.id,
            inviteId: member.id as string,
          });
        }
      }}
    >
      <Button
        variant="link"
        size="sm"
        color="primary"
        disabled={resendInvite.isPending}
      >
        <Trans message="Resend invite" />
      </Button>
      <ConfirmationDialog
        title={<Trans message="Resend invite" />}
        body={
          <Trans message="Are you sure you want to send this invite again?" />
        }
        confirm={<Trans message="Send" />}
      />
    </DialogTrigger>
  );
}
